import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MenuIcon, XIcon, BoxIcon } from "lucide-react";
import { Logo } from "./Logo";
import { NotificationBell } from "./NotificationBell";
import { ConsoleSwitcher } from "./ConsoleSwitcher";
import { AssistantDock } from "../intelligence/AssistantDock";
import { DataModeBanner } from "../platform/DataModeBanner";
import { ConsoleKey } from "../../types/identity";
export interface NavGroup {
  label: string;
  items: {
    to: string;
    label: string;
    icon: BoxIcon;
    end?: boolean;
  }[];
}
interface OperatorShellProps {
  consoleKey: ConsoleKey;
  consoleName: string;
  scopeLine: string;
  groups: NavGroup[];
  summary?: React.ReactNode;
}
export function OperatorShell({
  consoleKey,
  consoleName,
  scopeLine,
  groups,
  summary
}: OperatorShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebar = <div className="flex h-full flex-col" data-id="element-4594" data-mp="1vs7yo5x1l9g4">
      <div className="border-b border-line px-4 py-4" data-id="element-4595" data-mp="1vt0vof678kl2">
        <Logo subtitle={consoleName} data-id="element-4596" data-mp="1qt4tmlh2b385" />
        <p className="mt-3 text-caption leading-5 text-ink-muted" data-id="element-4597" data-mp="1qu6etirsl63l">{scopeLine}</p>
      </div>
      {summary && <div className="border-b border-line px-4 py-4" data-id="element-4598" data-mp="24tpb9eb8qt02">{summary}</div>}
      <nav aria-label="Console" className="flex-1 overflow-y-auto px-2 py-3" data-id="element-4599" data-mp="1ztbe4rb6h3gt">
        {groups.map(group => <div key={group.label} className="mb-4" data-id="element-4600" data-mp="2eqk69n8b2b9l">
            <p className="px-2 pb-1.5 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4601" data-mp="2ep9760pt1rot">
              {group.label}
            </p>
            <ul className="space-y-0.5" data-id="element-4602" data-mp="2njxbnonk35ck">
              {group.items.map(item => {
            const Icon = item.icon;
            return <li key={item.to} data-id="element-4603" data-mp="2jj3jgj32z4dv">
                    <NavLink to={item.to} end={item.end} onClick={() => setMobileOpen(false)} className={({
                isActive
              }) => `flex min-h-touch items-center gap-2.5 rounded-md px-2.5 py-2 text-body-sm font-medium transition-colors duration-150 ease-out ${isActive ? 'bg-brand-50 text-brand-800' : 'text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-4604" data-mp="2jk6b4t889auu">
                      <Icon className="h-4 w-4 shrink-0" data-id="element-4605" data-mp="1u6xjxzonqc33" />
                      {item.label}
                    </NavLink>
                  </li>;
          })}
            </ul>
          </div>)}
      </nav>
      <div className="border-t border-line px-4 py-3" data-id="element-4606" data-mp="23xs6l55tfdp2">
        <p className="text-caption leading-5 text-ink-subtle" data-id="element-4607" data-mp="28w3hlj7uju8v">
          Access is scoped to this organization. Actions are written to the Campuzon audit log.
        </p>
      </div>
    </div>;
  return <div className="flex min-h-full w-full bg-canvas" data-id="element-4608" data-mp="2hvqlcbo1tc53">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-line bg-surface lg:block" data-id="element-4609" data-mp="2hu5nod6ftd30">
        {sidebar}
      </aside>

      <AnimatePresence data-id="element-4610" data-mp="niqv7om1kzjk">
        {mobileOpen && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-4611" data-mp="njsak5a3c873">
            <motion.div className="absolute inset-0 bg-ink/40" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: 'easeOut'
        }} onClick={() => setMobileOpen(false)} data-id="element-4612" data-mp="sgsgmg6xxay7" />
            <motion.aside className="absolute inset-y-0 left-0 w-72 border-r border-line bg-surface" initial={{
          x: -24,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: -16,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-4613" data-mp="b782wzau911q">
              <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation" className="absolute right-2 top-2 flex h-touch w-touch items-center justify-center rounded-md text-ink-subtle hover:bg-canvas hover:text-ink" data-id="element-4614" data-mp="yvr6x2muuro0">
                <XIcon className="h-5 w-5" data-id="element-4615" data-mp="3eprib18f4cf4" />
              </button>
              {sidebar}
            </motion.aside>
          </div>}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-4616" data-mp="12eezw6yk68hn">
        <DataModeBanner data-id="element-4617" data-mp="12dn6n2sfomuj" />
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-surface px-4 sm:px-6" data-id="element-4618" data-mp="12cc230r5zb2d">
          <button type="button" onClick={() => setMobileOpen(true)} aria-label="Open navigation" className="flex h-touch w-touch shrink-0 items-center justify-center rounded-lg border border-line-strong text-ink-muted hover:text-ink lg:hidden" data-id="element-4619" data-mp="sg8wfkx3suao">
            <MenuIcon className="h-5 w-5" data-id="element-4620" data-mp="1l67rpy1w5nns" />
          </button>
          <p className="truncate text-body font-medium text-ink" data-id="element-4621" data-mp="1q3r9v91pmkl0">{consoleName}</p>
          <div className="ml-auto flex items-center gap-2" data-id="element-4622" data-mp="1c48bv92m4pmf">
            <AssistantDock data-id="element-4623" data-mp="1c3orzmtejr5r" />
            <NotificationBell data-id="element-4624" data-mp="1c6kpja1c8dto" />
            <ConsoleSwitcher current={consoleKey} data-id="element-4625" data-mp="1g5aaposmaznv" />
          </div>
        </header>
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6" data-id="element-4626" data-mp="pb8eu5olxvj">
          <Outlet data-id="element-4627" data-mp="nrqkz4dnern" />
        </main>
      </div>
    </div>;
}